"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Button, LinkButton, Modal } from "@/components/ui";
import { FlamingoMark } from "@/components/brand/FlamingoMark";
import {
  JOIN_PROMPT_DELAY_MS,
  JOIN_PROMPT_STORAGE_KEY,
  shouldPrompt,
} from "./joinPromptPolicy";

/**
 * Timed, once-per-visitor invitation shown to anonymous blog readers to feed
 * the `/join` funnel. Opens ~7s into an article, then suppresses itself for a
 * week via localStorage (stamped the moment it opens, so any dismissal still
 * counts as "seen"). Brand rule: flamingo/flame motif only, no romance copy.
 */
export function BlogJoinPrompt() {
  const t = useTranslations("blog.joinModal");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(JOIN_PROMPT_STORAGE_KEY);
    } catch {
      // Storage blocked (private mode / cookies off): skip silently.
      return;
    }
    if (!shouldPrompt(stored, Date.now())) return;

    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(JOIN_PROMPT_STORAGE_KEY, String(Date.now()));
      } catch {
        // Best effort; showing the prompt matters more than remembering it.
      }
      setOpen(true);
    }, JOIN_PROMPT_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <Modal open={open} onClose={() => setOpen(false)} title={t("title")}>
      <div className="relative">
        <FlamingoMark
          className="pointer-events-none absolute -right-6 -bottom-10"
          style={{ width: "10rem", opacity: 0.07 }}
        />
        <p className="text-md leading-relaxed text-white/70">{t("body")}</p>
        <div className="mt-6 flex flex-col gap-3">
          <LinkButton
            href="/join"
            size="lg"
            fullWidth
            onClick={() => setOpen(false)}
          >
            {t("cta")}
          </LinkButton>
          <Button variant="ghost" fullWidth onClick={() => setOpen(false)}>
            {t("dismiss")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
